import { useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import useCurrency from '../hooks/useCurrency';
import { BILLING_INTERVALS, getIntervalMonths } from '../utils/pricing';
import { openRazorpayCheckout } from '../utils/razorpayCheckout';
import {
  activateFreePlan,
  isGatewayUnavailable,
  razorpayOrgCheckout,
  stripeOrgCheckout,
  verifyCoupon,
  verifyRazorpayPayment,
} from '../api/billing';
import '../styles/plan-selection.css';

/* ---------------------------------------------------------------------------
   The plan step that runs straight after sign-up.

   The LOGIC here is a faithful port of handlePlanSelection() in the host's
   Auth/Register.js — same endpoints, same payloads, same gateway rules, same
   redirects. Only the presentation is new. The rules that are easy to get wrong
   and are therefore reproduced exactly:

     - Never choose a gateway before the server has resolved the currency,
       or a VPN/odd timezone sends an Indian customer to Stripe.
     - Indian customers go through Razorpay even for the free plan, because
       that call is what sets the org's paymentGateway.
     - A Razorpay failure only falls through to Stripe when the backend says
       the customer isn't Indian (GATEWAY_NOT_AVAILABLE / GATEWAY_DETECTION_FAILED).
       Any other Razorpay error for an Indian customer must surface and stop.
     - Coupons are checked against Razorpay's table first, then Stripe's, and
       memberCount sent for verification is total SEATS (members + admin).

   FREE_PLAN_HIDDEN: the Free card is hidden in the host too. Flip SHOW_FREE to
   bring it back in both places together.
   --------------------------------------------------------------------------- */

const SHOW_FREE = false;

const PLANS = [
  {
    id: 'standard',
    name: 'Standard',
    blurb: 'Everything a working team needs, tracked automatically.',
    features: [
      'Automatic time tracking, no timers',
      'App & website activity',
      'Productivity scoring',
      'AI work summaries, daily',
      'Projects, teams and roles',
    ],
  },
  {
    id: 'premium',
    name: 'Advanced',
    blurb: 'Adds the organisational brain on top.',
    highlight: true,
    features: [
      'Everything in Standard, plus:',
      'Ask TeamGrid & Pattern Intelligence',
      'Org Intelligence map',
      'CRM, HRMS and Email Insights',
      'Priority support',
    ],
  },
];

/* Billing terms come from utils/pricing so the marketing page, this checkout and
   the Razorpay backend all agree on the same four keys. */

export default function PlanSelection({ userId, registrationTokens, freePlanActive }) {
  const { currency, resolved: currencyResolved, symbol, price } = useCurrency();

  const [memberCount, setMemberCount] = useState(1);
  /* The billing term drives Razorpay: 'quarterly' buys a real 3-month recurring
     subscription on its own plan. Stripe has no 3/6-month prices, so the legacy
     `months` prepay number is derived from the term and only sent to Stripe. */
  const [term, setTerm] = useState('monthly');
  const months = getIntervalMonths(term);
  const [selected, setSelected] = useState('premium');
  const [processing, setProcessing] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [couponCode, setCouponCode] = useState('');
  const [couponState, setCouponState] = useState(null); // null | 'checking' | 'valid' | 'invalid'
  const [couponDiscount, setCouponDiscount] = useState('');
  const [actualCouponId, setActualCouponId] = useState(null);

  const isIndian = currency === 'INR';
  const totalSeats = memberCount + 1; // +1 for the admin, same as the host

  /* Grouped digits: ₹3,894 not ₹3894. INR groups differently from USD above
     four digits, so the locale follows the detected currency. */
  const grouped = (n) =>
    Number(n).toLocaleString(isIndian ? 'en-IN' : 'en-US', { maximumFractionDigits: 2 });

  const total = useMemo(
    () => (totalSeats * (price(selected, term) || 0)).toFixed(isIndian ? 0 : 2),
    [totalSeats, term, selected, price, isIndian],
  );

  /* Re-check the coupon when the seat count changes — the host does the same,
     because discounts can be seat-dependent. */
  useEffect(() => {
    if (couponState !== 'valid' || !couponCode.trim()) return undefined;
    let cancelled = false;
    verifyCoupon({ couponCode, memberCount: memberCount + 1 })
      .then((r) => {
        if (cancelled) return;
        if (r?.data?.valid) {
          setCouponDiscount(r.data.discount || '');
          setActualCouponId(r.data.couponCode || null);
        } else {
          setCouponState('invalid');
          setCouponDiscount('');
          setActualCouponId(null);
        }
      })
      .catch(() => { /* leave the previous verdict in place */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberCount]);

  const applyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      setCouponState(null);
      setCouponDiscount('');
      setActualCouponId(null);
      return;
    }
    setCouponState('checking');
    try {
      const r = await verifyCoupon({ couponCode: code, memberCount: totalSeats });
      if (r?.data?.valid) {
        setCouponState('valid');
        setCouponDiscount(r.data.discount || '');
        setActualCouponId(r.data.couponCode || null);
      } else {
        setCouponState('invalid');
        setCouponDiscount('');
        setActualCouponId(null);
      }
    } catch (e) {
      setCouponState('invalid');
      setCouponDiscount('');
      setActualCouponId(null);
      setError(e?.message || 'Could not check that coupon.');
    }
  };

  const storeTokens = (tokens) => {
    if (!tokens?.token) return;
    try {
      localStorage.setItem('token', tokens.token);
      if (tokens.refreshToken) localStorage.setItem('refreshToken', tokens.refreshToken);
      window.dispatchEvent(new Event('auth-token-changed'));
    } catch { /* private mode */ }
  };

  const choose = async (plan) => {
    setError('');
    /* Gateway choice must wait for server-side currency detection. */
    if (!currencyResolved) {
      setNotice('Detecting your region, one moment…');
      return;
    }
    setNotice('');
    setSelected(plan);
    setProcessing(plan);

    try {
      if (plan === 'free') {
        if (freePlanActive && registrationTokens && !isIndian) {
          storeTokens(registrationTokens);
          window.location.assign('/dashboard');
          return;
        }
        const r = await activateFreePlan({ userId, plan, couponCode: actualCouponId, isIndian });
        storeTokens(r?.data?.token ? r.data : registrationTokens);
        window.location.assign('/dashboard');
        return;
      }

      if (memberCount < 1 || memberCount > 1000) {
        setError('Enter a team size between 1 and 1000.');
        setProcessing(null);
        return;
      }

      /* --- Razorpay first --- */
      let rzp = null;
      let gatewayUnavailable = false;
      try {
        rzp = await razorpayOrgCheckout({
          userId, plan, memberCount, interval: term, couponCode,
        });
      } catch (e) {
        gatewayUnavailable = isGatewayUnavailable(e);
        if (!gatewayUnavailable && isIndian) {
          setError(e?.message || 'Could not start Razorpay checkout. Please try again or contact support.');
          setProcessing(null);
          return;
        }
        rzp = null;
      }

      const rzpUsable = !!(rzp?.data?.subscriptionId || rzp?.data?.orderId);
      if (!rzpUsable && !gatewayUnavailable && isIndian) {
        setError(rzp?.message || 'Could not start Razorpay checkout. Please try again or contact support.');
        setProcessing(null);
        return;
      }

      if (rzpUsable) {
        const d = rzp.data;
        openRazorpayCheckout({
          razorpayKeyId: d.razorpayKeyId,
          amount: d.amount,
          currency: d.currency,
          orderId: d.orderId,
          subscriptionId: d.subscriptionId,
          customerName: d.customerName,
          customerEmail: d.customerEmail,
          notes: d.notes,
          description: `TeamGrid ${plan} Plan`,
          onSuccess: async (payment) => {
            try {
              const v = await verifyRazorpayPayment({
                razorpay_order_id: payment.razorpay_order_id,
                razorpay_payment_id: payment.razorpay_payment_id,
                razorpay_signature: payment.razorpay_signature,
                razorpay_subscription_id: payment.razorpay_subscription_id,
                userId,
                organizationId: d.notes?.organizationId,
                plan,
                memberCount,
                totalSeats: d.totalSeats,
                interval: term,
                type: 'organization_subscription',
                couponCode: d.appliedCoupon || null,
              });
              if (v?.data?.token) storeTokens(v.data);
              window.location.assign('/dashboard?payment=success');
            } catch {
              setError('Payment verification failed. Please contact support before trying again.');
              setProcessing(null);
            }
          },
          onFailure: (err) => {
            setError(err?.description || 'Payment failed. Please try again.');
            setProcessing(null);
          },
          onDismiss: () => setProcessing(null),
        });
        return;
      }

      /* --- Stripe (international) --- */
      /* Stripe now carries the same four terms as Razorpay. */
      const s = await stripeOrgCheckout({ userId, plan, memberCount, interval: term, couponCode });
      if (s?.data?.url) {
        window.location.assign(s.data.url);
        return;
      }
      setError(s?.message || 'Could not start checkout. Please try again.');
      setProcessing(null);
    } catch (e) {
      setError(e?.message || 'Could not process that plan. Please try again.');
      setProcessing(null);
    }
  };

  const visible = SHOW_FREE ? [{ id: 'free', name: 'Free', blurb: '', features: [] }, ...PLANS] : PLANS;

  return (
    <div className="ps-wrap" role="dialog" aria-modal="true" aria-labelledby="ps-title">
      <div className="ps-card">
        <header className="ps-head">
          <img className="ps-mark" src="/assets/mark-teamgrid.svg" alt="" />
          <h1 id="ps-title">Your workspace is ready</h1>
          <p>Pick a plan to switch it on. Every plan starts with a 7-day free trial.</p>
        </header>

        <div className="ps-controls">
          <label className="ps-field">
            <span>Team members <small>(besides you)</small></span>
            <input
              type="number"
              min="1"
              max="1000"
              value={memberCount}
              onChange={(e) => setMemberCount(Math.max(1, Math.min(1000, Number(e.target.value) || 1)))}
            />
          </label>

          <div className="ps-field ps-term">
            <span id="ps-term-label">Billing cycle</span>
            <Select
              aria-labelledby="ps-term-label"
              value={term}
              onChange={setTerm}
              options={BILLING_INTERVALS.map((o) => ({ value: o.key, label: o.label }))}
              popupClassName="ps-term-dropdown"
              /* Render the dropdown inside .tg-landing rather than antd's default
                 document.body portal. Two reasons: the scoped stylesheet can then
                 reach it (scope-css.cjs prefixes every rule with .tg-landing), and
                 it scrolls with .ps-wrap instead of floating over it. */
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
              popupMatchSelectWidth
              style={{ width: '100%' }}
            />
          </div>

          <div className="ps-field ps-coupon">
            <span>Coupon <small>(optional)</small></span>
            <div className="ps-coupon-row">
              <input
                type="text"
                placeholder="Enter coupon or promotion code"
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value); setCouponState(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
              />
              <button type="button" onClick={applyCoupon} disabled={couponState === 'checking'}>
                {couponState === 'checking' ? 'Checking…' : 'Apply'}
              </button>
            </div>
            {couponState === 'valid' && <p className="ps-ok">Coupon applied{couponDiscount ? ` — ${couponDiscount}` : ''}</p>}
            {couponState === 'invalid' && <p className="ps-bad">That code isn’t valid.</p>}
          </div>
        </div>

        <div className="ps-plans">
          {visible.map((p) => (
            <div
              key={p.id}
              className={`ps-plan${selected === p.id ? ' on' : ''}${p.highlight ? ' hero' : ''}`}
              onClick={() => setSelected(p.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(p.id); } }}
              aria-pressed={selected === p.id}
            >
              {p.highlight && <span className="ps-tag">MOST CHOSEN</span>}
              <h3>{p.name}</h3>
              <div className="ps-price">
                {symbol}{grouped(price(p.id, term))}
                <small> / user / {term === 'monthly' ? 'mo' : `${months} mo`}</small>
              </div>
              {term !== 'monthly' && (
                <div className="ps-price-eq">
                  {symbol}{price(p.id)} / user / mo · billed every {months} months
                </div>
              )}
              <p className="ps-blurb">{p.blurb}</p>
              <ul>
                {p.features.map((f) => (
                  <li key={f}>
                    <svg aria-hidden="true" viewBox="0 0 16 16"><path fill="none" stroke="#007A6E" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" d="M2 8.5 6 12.5 14 3.5" /></svg>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="ps-cta"
                onClick={(e) => { e.stopPropagation(); choose(p.id); }}
                disabled={!!processing}
              >
                {processing === p.id ? 'Starting checkout…' : `Choose ${p.name}`}
              </button>
            </div>
          ))}
        </div>

        <div className="ps-total">
          <span>
            {totalSeats} seat{totalSeats > 1 ? 's' : ''}
            {term === 'monthly' ? ' · billed monthly' : ` · billed every ${months} months`}
          </span>
          <strong>{symbol}{grouped(total)}</strong>
        </div>

        {notice && <p className="ps-notice" role="status">{notice}</p>}
        {error && <p className="ps-bad" role="alert">{error}</p>}

        <p className="ps-fine">
          Prices in {currency}. Taxes may apply at checkout. You can change seats or plan any time from Billing.
        </p>
      </div>
    </div>
  );
}
